import type { Role } from "@agentry/zerovia-contracts";
import type { ReactNode } from "react";
import { Link, Navigate, useLocation } from "react-router";
import { EmptyState, ForbiddenState, LoadingState } from "../ui/states";
import { useAuth } from "./AuthProvider";
import { type Area, canAccessRoles } from "./navigation";

/**
 * Route単位の認可。Menuの出し分けとは別に、直接URLでも必ずここを通す。
 *
 * - 未Login: Loginへ送り、戻り先を保持する
 * - Login済みで権限不足: Forbiddenを表示する（Loginへループさせない）
 */
export function RoleGuard({
	area,
	roles,
	children,
	requireScope = true,
}: {
	area: Area;
	roles: readonly Role[];
	children: ReactNode;
	requireScope?: boolean;
}) {
	const { state, role, scope } = useAuth();
	const location = useLocation();

	if (state.status === "loading") {
		return (
			<div className="mx-auto max-w-3xl px-4 py-16">
				<LoadingState label="アクセス権を確認しています" />
			</div>
		);
	}

	if (state.status === "anonymous") {
		if (state.reason === "logged-out") {
			return <Navigate to="/login?reason=logged-out" replace />;
		}
		const next = `${location.pathname}${location.search}`;
		return <Navigate to={`/login?reason=auth-required&next=${encodeURIComponent(next)}`} replace />;
	}

	if (!canAccessRoles(roles, role)) {
		return (
			<div className="mx-auto max-w-3xl px-4 py-16">
				<ForbiddenState detail={forbiddenDetail(area)} />
			</div>
		);
	}

	if (requireScope && !scope) {
		return (
			<div className="mx-auto max-w-3xl px-4 py-16">
				<EmptyState
					title="利用可能な Project がありません"
					description="管理者にお問い合わせください"
				/>
			</div>
		);
	}

	return <>{children}</>;
}

/** Project未所属の空状態だけをAppShell内に配置するための内側Guard。
 * ワークスペース一覧は「未作成から作成する」入口なので Project 無しでも通す。
 */
export function ProjectScopeGuard({ children }: { children: ReactNode }) {
	const { scope } = useAuth();
	const location = useLocation();
	const allowWithoutProject =
		location.pathname === "/app/workspace" || location.pathname === "/app/workspace/";

	if (!scope && !allowWithoutProject) {
		return (
			<div className="mx-auto max-w-3xl py-10">
				<EmptyState
					title="利用可能なワークスペースがありません"
					description="先にワークスペースを作成してください。"
					action={
						<Link
							to="/app/workspace"
							className="rounded-lg border border-ink-300 bg-white px-4 py-2 text-[14px] font-semibold text-ink-800 hover:bg-ink-50"
						>
							ワークスペースへ
						</Link>
					}
				/>
			</div>
		);
	}
	return <>{children}</>;
}

function forbiddenDetail(area: Area): string {
	switch (area) {
		case "tenant-admin":
			return "組織の管理画面はTenant Adminのみが利用できます。";
		case "system-admin":
			return "製品管理画面はAgentry System Adminのみが利用できます。";
		default:
			return "この画面を表示する権限が割り当てられていません。";
	}
}
